import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RiskBadge, StatusPill } from "@/components/RiskBadge";
import AttachmentsSection from "@/components/AttachmentsSection";
import ActivityTimeline from "@/components/ActivityTimeline";
import StatusStepper from "@/components/StatusStepper";
import {
  ArrowLeft,
  PaperPlaneTilt,
  ChatCircle,
  Sparkle,
  CheckCircle,
  XCircle,
  Question,
  Trash,
  Buildings,
  CalendarBlank,
  PencilSimple,
  FloppyDisk,
  X,
  ArrowsClockwise,
  Paperclip,
} from "@phosphor-icons/react";

const STATUSES = ["Open", "In-Progress", "Resolved", "Closed"];
const RISKS = ["High", "Medium", "Low"];

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString();
}

export default function ObservationDetail() {
  const { obsId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAudit = user?.role === "audit_team";

  const [obs, setObs] = useState(null);
  const [company, setCompany] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [resolutions, setResolutions] = useState([]);
  const [comments, setComments] = useState([]);
  const [draftComment, setDraftComment] = useState("");
  const [resForm, setResForm] = useState({ client_response: "", action_taken: "" });
  const [submittingRes, setSubmittingRes] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const commentEndRef = useRef(null);

  // Edit-mode state for observation details (audit only)
  const [editMode, setEditMode] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [detailsForm, setDetailsForm] = useState({
    area: "",
    observation: "",
    impact: "",
    recommendation: "",
    risk: "Medium",
  });

  const loadAll = async () => {
    const o = await api.get(`/observations/${obsId}`);
    setObs(o.data);
    const [c, res, comm] = await Promise.all([
      api.get(`/companies/${o.data.company_id}`),
      api.get(`/observations/${obsId}/resolutions`),
      api.get(`/observations/${obsId}/comments`),
    ]);
    setCompany(c.data);
    setResolutions(res.data);
    setComments(comm.data);

    // load cycle name
    try {
      const cycles = await api.get(`/cycles?company_id=${o.data.company_id}`);
      setCycle(cycles.data.find((cy) => cy.id === o.data.cycle_id));
    } catch (_) {}
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [obsId]);

  const reloadCommentsAndScroll = async () => {
    const r = await api.get(`/observations/${obsId}/comments`);
    setComments(r.data);
    setTimeout(() => commentEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const postComment = async (e) => {
    e.preventDefault();
    const m = draftComment.trim();
    if (!m) return;
    setPostingComment(true);
    try {
      await api.post(`/observations/${obsId}/comments`, { message: m });
      setDraftComment("");
      await reloadCommentsAndScroll();
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setPostingComment(false);
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/observations/${obsId}/comments/${cid}`);
      const r = await api.get(`/observations/${obsId}/comments`);
      setComments(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const submitResolution = async (e) => {
    e.preventDefault();
    setSubmittingRes(true);
    try {
      await api.post(`/observations/${obsId}/resolution`, resForm);
      toast.success("Resolution submitted");
      setResForm({ client_response: "", action_taken: "" });
      await loadAll();
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error("Submit failed");
    } finally {
      setSubmittingRes(false);
    }
  };

  const reviewResolution = async (rid, action) => {
    const comment = window.prompt(`${action} — add comment (optional):`, "") || "";
    try {
      await api.put(`/resolutions/${rid}/review`, {
        review_status: action,
        auditor_comment: comment,
      });
      toast.success(`Resolution ${action}`);
      await loadAll();
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error("Review failed");
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.put(`/observations/${obsId}`, { status: newStatus });
      toast.success("Status updated");
      const o = await api.get(`/observations/${obsId}`);
      setObs(o.data);
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const startEdit = () => {
    setDetailsForm({
      area: obs.area || "",
      observation: obs.observation || "",
      impact: obs.impact || "",
      recommendation: obs.recommendation || "",
      risk: obs.risk || "Medium",
    });
    setEditMode(true);
  };

  const cancelEdit = () => setEditMode(false);

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      const r = await api.put(`/observations/${obsId}`, detailsForm);
      setObs(r.data);
      toast.success("Observation updated");
      setEditMode(false);
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingDetails(false);
    }
  };

  const recomputeAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/observations/${obsId}/recompute-ai`);
      setObs(r.data);
      toast.success(`AI re-assessed — ${r.data.risk} risk (weight ${r.data.ai_risk_weight})`);
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI re-run failed");
    } finally {
      setAiBusy(false);
    }
  };

  if (!obs) {
    return (
      <AppLayout>
        <div className="text-sm text-slate-500">Loading...</div>
      </AppLayout>
    );
  }

  const backTo = isAudit
    ? `/audit/companies/${obs.company_id}`
    : `/client/observations`;

  const header = (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <Link to={backTo} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Back
        </Link>
        <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
          <Buildings size={12} /> {company?.name}
          {cycle && (
            <>
              <span>·</span>
              <CalendarBlank size={12} /> {cycle.name}
            </>
          )}
          {obs.rolled_forward_from && (
            <span className="text-[10px] uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded ml-1">
              Rolled forward
            </span>
          )}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Geist" }}>
          Observation #{obs.sr_no} — {obs.area}
        </h1>
      </div>
      <div className="flex items-center gap-2">
        <RiskBadge risk={obs.risk} />
        {isAudit ? (
          <Select value={obs.status} onValueChange={changeStatus}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="detail-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <StatusPill status={obs.status} />
        )}
      </div>
    </div>
  );

  return (
    <AppLayout header={header}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-5 fade-up" data-testid="status-stepper-card">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-4">Observation Lifecycle</div>
            <StatusStepper status={obs.status} />
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5 fade-up fade-up-1" data-testid="obs-detail-card">
            <div className="flex items-start justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-800 font-bold">Observation Details</div>
              {isAudit && !editMode && (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={recomputeAI}
                    disabled={aiBusy}
                    data-testid="recompute-ai-btn"
                  >
                    <ArrowsClockwise size={12} className={`mr-1 ${aiBusy ? "animate-spin" : ""}`} />
                    {aiBusy ? "Re-assessing..." : "Re-run AI"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={startEdit}
                    data-testid="edit-details-btn"
                  >
                    <PencilSimple size={12} className="mr-1" /> Edit
                  </Button>
                </div>
              )}
              {isAudit && editMode && (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={cancelEdit}
                    disabled={savingDetails}
                    data-testid="cancel-details-btn"
                  >
                    <X size={12} className="mr-1" /> Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-slate-900 hover:bg-slate-800"
                    onClick={saveDetails}
                    disabled={savingDetails}
                    data-testid="save-details-btn"
                  >
                    <FloppyDisk size={12} className="mr-1" /> {savingDetails ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </div>

            {!editMode ? (
              <>
                <p className="text-sm text-slate-800 mt-1 whitespace-pre-line leading-relaxed" data-testid="detail-observation-text">{obs.observation}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-800 font-bold">Impact</div>
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-line leading-relaxed" data-testid="detail-impact-text">{obs.impact || "—"}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-800 font-bold">Recommendation</div>
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-line leading-relaxed" data-testid="detail-reco-text">{obs.recommendation || "—"}</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-3 mt-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Area / Department</Label>
                    <Input
                      value={detailsForm.area}
                      onChange={(e) => setDetailsForm({ ...detailsForm, area: e.target.value })}
                      data-testid="edit-area-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Risk</Label>
                    <Select value={detailsForm.risk} onValueChange={(v) => setDetailsForm({ ...detailsForm, risk: v })}>
                      <SelectTrigger data-testid="edit-risk-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RISKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Observation</Label>
                  <Textarea
                    rows={3}
                    value={detailsForm.observation}
                    onChange={(e) => setDetailsForm({ ...detailsForm, observation: e.target.value })}
                    data-testid="edit-observation-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Impact</Label>
                    <Textarea
                      rows={3}
                      value={detailsForm.impact}
                      onChange={(e) => setDetailsForm({ ...detailsForm, impact: e.target.value })}
                      data-testid="edit-impact-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Recommendation</Label>
                    <Textarea
                      rows={3}
                      value={detailsForm.recommendation}
                      onChange={(e) => setDetailsForm({ ...detailsForm, recommendation: e.target.value })}
                      data-testid="edit-reco-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {obs.ai_rationale && !editMode && (
              <div className="mt-5 border border-blue-200 bg-blue-50/50 rounded-md p-3">
                <div className="flex items-center gap-1.5 text-blue-700 text-[10px] uppercase tracking-wider font-semibold">
                  <Sparkle size={11} weight="fill" /> AI Rationale · weight {obs.ai_risk_weight}/100
                </div>
                <p className="text-sm text-slate-700 mt-1 italic">{obs.ai_rationale}</p>
              </div>
            )}
          </div>

          <Tabs defaultValue="discussion" className="w-full">
            <TabsList data-testid="detail-tabs">
              <TabsTrigger value="discussion" data-testid="tab-discussion">
                Discussion {comments.length > 0 && <span className="ml-1.5 text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{comments.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="resolutions" data-testid="tab-resolutions">
                Resolutions {resolutions.length > 0 && <span className="ml-1.5 text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{resolutions.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="activity-tab" data-testid="tab-detail-activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="discussion" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <h3 className="text-base font-semibold flex items-center gap-1.5" style={{ fontFamily: "Geist" }}>
                  <ChatCircle size={16} /> Discussion Thread
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Ask clarifications, share context — audit team & client can both post.</p>

                <div className="mt-4 space-y-3 max-h-[460px] overflow-y-auto pr-1" data-testid="comments-list">
                  {comments.length === 0 && (
                    <div className="text-sm text-slate-500 py-6 text-center" data-testid="no-comments">No messages yet. Start the conversation below.</div>
                  )}
                  {comments.map((c) => {
                    const mine = c.author_id === user.id;
                    const isAuditor = c.author_role === "audit_team";
                    return (
                      <div key={c.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`comment-${c.id}`}>
                        <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold ${isAuditor ? "bg-slate-900 text-white" : "bg-blue-600 text-white"}`}>
                          {c.author_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className={`max-w-[80%] ${mine ? "items-end text-right" : ""}`}>
                          <div className="text-[11px] text-slate-500 mb-0.5">
                            <span className="font-medium text-slate-700">{c.author_name}</span>
                            <span className="mx-1.5">·</span>
                            <span className={isAuditor ? "text-slate-600" : "text-blue-700"}>{isAuditor ? "Auditor" : "Client"}</span>
                            <span className="mx-1.5">·</span>
                            <span>{fmtTime(c.created_at)}</span>
                          </div>
                          <div className={`inline-block px-3 py-2 rounded-md border text-sm whitespace-pre-line ${
                            isAuditor ? "bg-slate-50 border-slate-200 text-slate-800" : "bg-blue-50 border-blue-200 text-slate-800"
                          }`}>
                            {c.message}
                          </div>
                          {(mine || isAudit) && (
                            <button
                              onClick={() => deleteComment(c.id)}
                              className="text-[10px] text-slate-400 hover:text-red-600 ml-2 mt-0.5"
                              data-testid={`delete-comment-${c.id}`}
                            >
                              <Trash size={10} className="inline mr-0.5" /> delete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={commentEndRef} />
                </div>

                <form onSubmit={postComment} className="mt-4 flex gap-2 items-end border-t border-slate-100 pt-4">
                  <div className="flex-1">
                    <Textarea
                      rows={2}
                      value={draftComment}
                      onChange={(e) => setDraftComment(e.target.value)}
                      placeholder="Write a message…"
                      data-testid="comment-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postComment(e);
                      }}
                    />
                    <div className="text-[10px] text-slate-400 mt-1">Tip: ⌘/Ctrl + Enter to send</div>
                  </div>
                  <Button
                    type="submit"
                    disabled={postingComment || !draftComment.trim()}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="send-comment-btn"
                  >
                    <PaperPlaneTilt size={14} className="mr-1" /> Send
                  </Button>
                </form>
              </div>
            </TabsContent>

            <TabsContent value="resolutions" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>Resolutions</h3>
                <p className="text-xs text-slate-500 mt-0.5">Official record of client actions. Audit team reviews each submission.</p>

                <div className="mt-4 space-y-3" data-testid="resolutions-list">
                  {resolutions.length === 0 && (
                    <div className="text-sm text-slate-500 py-6 text-center" data-testid="no-resolutions">No resolutions yet.</div>
                  )}
                  {resolutions.map((r) => (
                    <div key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`resolution-detail-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{fmtTime(r.submitted_at)}</div>
                        <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${
                          r.review_status === "Accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          r.review_status === "Rejected" ? "bg-red-50 text-red-700 border-red-200" :
                          r.review_status === "Needs More Info" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          "bg-slate-50 text-slate-600 border-slate-200"
                        }`}>{r.review_status}</span>
                      </div>
                      <div className="mt-2 text-sm"><span className="text-slate-500 text-xs">Response: </span>{r.client_response}</div>
                      <div className="text-sm"><span className="text-slate-500 text-xs">Action taken: </span>{r.action_taken}</div>
                      {r.auditor_comment && (
                        <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                          <b>Auditor:</b> {r.auditor_comment}
                        </div>
                      )}
                      {isAudit && r.review_status === "Pending Review" && (
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => reviewResolution(r.id, "Accepted")} data-testid={`detail-accept-${r.id}`}>
                            <CheckCircle size={13} className="mr-1" /> Accept & Resolve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => reviewResolution(r.id, "Needs More Info")} data-testid={`detail-needsinfo-${r.id}`}>
                            <Question size={13} className="mr-1" /> Needs more info
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => reviewResolution(r.id, "Rejected")} data-testid={`detail-reject-${r.id}`}>
                            <XCircle size={13} className="mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {!isAudit && (
                  <form onSubmit={submitResolution} className="mt-5 border-t border-slate-100 pt-4 space-y-3">
                    <div className="flex items-center gap-1.5 text-blue-700 text-xs">
                      <Sparkle size={12} weight="fill" /> Submit a new resolution to reduce risk
                    </div>
                    <div>
                      <Label>Your Response</Label>
                      <Textarea rows={2} required value={resForm.client_response} onChange={(e) => setResForm({ ...resForm, client_response: e.target.value })} placeholder="Acknowledged. We have reviewed the observation..." data-testid="detail-response-input" />
                    </div>
                    <div>
                      <Label>Action Taken / Plan</Label>
                      <Textarea rows={2} required value={resForm.action_taken} onChange={(e) => setResForm({ ...resForm, action_taken: e.target.value })} placeholder="Implemented reconciliation between Tally and Zoho on 15-May-2026..." data-testid="detail-action-input" />
                    </div>

                    {/* Inline evidence uploader for clients */}
                    <div className="border border-dashed border-blue-300 bg-blue-50/40 rounded-md p-3" data-testid="resolution-evidence-uploader">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-semibold text-[#0B2A5B] flex items-center gap-1">
                          <Paperclip size={12} /> Supporting Documents
                        </div>
                        <span className="text-[10px] text-slate-500">Optional · PDF, Excel, images up to 10 MB</span>
                      </div>
                      <p className="text-[11px] text-slate-600 mb-2">
                        Attach evidence (policy docs, screenshots, signed approvals) to strengthen your resolution. Files are visible to the audit team.
                      </p>
                      <AttachmentsSection observationId={obs.id} role={user.role} compact />
                    </div>

                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={submittingRes} data-testid="detail-submit-resolution-btn">
                      <PaperPlaneTilt size={14} className="mr-1" /> {submittingRes ? "Submitting..." : "Submit Resolution"}
                    </Button>
                  </form>
                )}
              </div>
            </TabsContent>

            <TabsContent value="activity-tab" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Geist" }}>Observation Activity</h3>
                <ActivityTimeline companyId={obs.company_id} observationId={obs.id} />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: attachments + meta */}
        <div className="space-y-4">
          <AttachmentsSection observationId={obs.id} role={user.role} />

          <div className="bg-white border border-slate-200 rounded-md p-5 text-sm" data-testid="detail-meta">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Metadata</div>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">Created</span><span className="text-slate-800">{fmtTime(obs.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Updated</span><span className="text-slate-800">{fmtTime(obs.updated_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">AI Weight</span><span className="font-semibold tabular-nums">{obs.ai_risk_weight}/100</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Risk</span><RiskBadge risk={obs.risk} /></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><StatusPill status={obs.status} /></div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
